import type { PropertySensorGroupRow } from '@metron/shared';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { SensorGroupSourceTag, SensorGroupTypeTag } from './SensorGroupTags.tsx';
import { Button } from '../ui/button.tsx';
import { Trash2 } from 'lucide-react';

interface SensorGroupAssignmentProps {
  sensorGroup: PropertySensorGroupRow;
  propertyId: number;
  availableSensorGroups: PropertySensorGroupRow[];
  onAddMaster?: (childSensorGroupId: number, newMasterId: number) => void;
  onRemoveMaster?: (masterSensorGroupId: number) => void;
  onChildChange?: (sensorGroupId: number, newChildId: number | null) => void;
  onStandaloneChange?: (oldSensorGroupId: number, newSensorGroupId: number | null) => void;
  onRemove?: () => void;
  getEligibleForMasterRole: (excludeId?: number) => PropertySensorGroupRow[];
  getEligibleForChildRole: (propertyId: number, excludeId?: number) => PropertySensorGroupRow[];
  getEligibleForStandaloneAssignment: () => PropertySensorGroupRow[];
}

/**
 * Displays a single sensor group assignment in a card layout.
 * Shows master/child relationships with clear visual hierarchy.
 * Provides functional dropdowns for assigning master/child relationships.
 */
export function SensorGroupAssignment({
  sensorGroup,
  propertyId,
  availableSensorGroups,
  onAddMaster,
  onRemoveMaster,
  onChildChange,
  onStandaloneChange,
  onRemove: _onRemove,
  getEligibleForMasterRole,
  getEligibleForChildRole,
  getEligibleForStandaloneAssignment,
}: SensorGroupAssignmentProps) {
  const hasParent = !!sensorGroup.parentSensorGroupId;
  const hasChild = !!sensorGroup.childSensorGroupId;

  // Get eligible sensor groups for master, child, and standalone roles
  const eligibleMasters = getEligibleForMasterRole(sensorGroup.sensorGroupId);
  const eligibleChildren = getEligibleForChildRole(propertyId, sensorGroup.sensorGroupId);
  const eligibleStandalone = getEligibleForStandaloneAssignment();

  // Find currently assigned parent and child from all available groups
  const currentParent = sensorGroup.parentSensorGroupId
    ? availableSensorGroups.find((sg) => sg.sensorGroupId === sensorGroup.parentSensorGroupId)
    : null;
  const currentChild = sensorGroup.childSensorGroupId
    ? availableSensorGroups.find((sg) => sg.sensorGroupId === sensorGroup.childSensorGroupId)
    : null;

  // Combine current selections with eligible options to ensure they appear in dropdowns
  // For master: include current parent if it exists and isn't already in eligible list
  const masterOptions =
    currentParent && !eligibleMasters.find((sg) => sg.sensorGroupId === currentParent.sensorGroupId)
      ? [currentParent, ...eligibleMasters]
      : eligibleMasters;

  // For child: include current child if it exists and isn't already in eligible list
  // Also, if this sensor group IS a child (hasParent), include itself in the options
  let childOptions = eligibleChildren;

  // Add the current child from master's perspective (when viewing a master with a child)
  if (currentChild && !childOptions.find((sg) => sg.sensorGroupId === currentChild.sensorGroupId)) {
    childOptions = [currentChild, ...childOptions];
  }

  // Add this sensor group itself if it's a child (when viewing the child's card)
  if (hasParent && !childOptions.find((sg) => sg.sensorGroupId === sensorGroup.sensorGroupId)) {
    childOptions = [sensorGroup, ...childOptions];
  }

  // For standalone: include current sensor group itself and eligible standalone groups
  let standaloneOptions = eligibleStandalone;
  if (
    !hasParent && !hasChild &&
    !standaloneOptions.find((sg) => sg.sensorGroupId === sensorGroup.sensorGroupId)
  ) {
    standaloneOptions = [sensorGroup, ...standaloneOptions];
  }

  // TEMPORARY WORKAROUND: Check if DMA type is involved in the relationship
  // DMA groups are currently broken in the legacy system, so we need to prevent
  // modification of these relationships until the legacy system is fixed.
  // TODO: Remove this check once legacy system DMA issue is resolved
  const hasDMAInRelationship = sensorGroup.waterscopeGroupType === 'DMA' ||
    (currentParent && currentParent.waterscopeGroupType === 'DMA') ||
    (currentChild && currentChild.waterscopeGroupType === 'DMA');

  // Helper to render sensor group option
  const renderSensorGroupOption = (sg: PropertySensorGroupRow) => (
    <div className='flex items-center gap-2'>
      <span className='font-medium'>{sg.sensorGroupName}</span>
      {sg.sensorGroupMetronId && (
        <span className='text-sm text-muted-foreground'>
          ({sg.sensorGroupMetronId})
        </span>
      )}
      <SensorGroupSourceTag source={sg.sensorGroupSource} />
      <SensorGroupTypeTag type={sg.waterscopeGroupType} />
    </div>
  );

  return (
    <div className='rounded-lg border bg-muted/30 p-4'>
      <div className='flex items-start justify-between gap-4'>
        <div className='flex-1 space-y-2'>
          {/* Parent/Master Sensor Group Row */}
          {hasParent
            ? (
              <div className='flex items-center gap-2'>
                <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wide w-24 shrink-0 text-right'>
                  Master:
                </span>
                <div className='flex-1'>
                  <Select
                    value={sensorGroup.parentSensorGroupId?.toString() || 'none'}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        onRemoveMaster?.(sensorGroup.parentSensorGroupId!);
                      } else {
                        onAddMaster?.(sensorGroup.sensorGroupId, Number(value));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None (Remove Master)</SelectItem>
                      {masterOptions.map((sg) => (
                        <SelectItem key={sg.sensorGroupId} value={sg.sensorGroupId.toString()}>
                          {renderSensorGroupOption(sg)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  className='border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground w-48 shrink-0 justify-start'
                  onClick={() => {
                    if (hasDMAInRelationship) {
                      // For DMA relationships, use onStandaloneChange to remove the entire assignment
                      onStandaloneChange?.(sensorGroup.sensorGroupId, null);
                    } else {
                      sensorGroup.parentSensorGroupId &&
                        onRemoveMaster?.(sensorGroup.parentSensorGroupId);
                    }
                  }}
                  title={hasDMAInRelationship
                    ? 'Remove assignment from property (DMA groups cannot have relationships modified due to legacy system constraints)'
                    : 'Remove master (break relationship)'}
                >
                  <Trash2 className='h-4 w-4 mr-1' />
                  {hasDMAInRelationship ? 'Remove Association' : 'Remove Master'}
                </Button>
              </div>
            )
            : !hasChild
            ? (
              <div className='flex items-center gap-2'>
                <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wide w-24 shrink-0 text-right'>
                  Master:
                </span>
                <div className='flex-1'>
                  <Select
                    value='none'
                    onValueChange={(value) => {
                      if (value !== 'none') {
                        onAddMaster?.(sensorGroup.sensorGroupId, Number(value));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select master group...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None</SelectItem>
                      {masterOptions.map((sg) => (
                        <SelectItem key={sg.sensorGroupId} value={sg.sensorGroupId.toString()}>
                          {renderSensorGroupOption(sg)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='w-48 shrink-0' />
              </div>
            )
            : null}

          {/* Main Sensor Group Row */}
          <div className='flex items-center gap-2'>
            <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wide w-24 shrink-0 text-right'>
              {hasParent ? 'Child:' : hasChild ? 'Master:' : 'Standalone:'}
            </span>
            <div className='flex-1'>
              {hasParent
                ? (
                  // When this is a child in a relationship, show editable child selector
                  <Select
                    value={sensorGroup.sensorGroupId.toString()}
                    onValueChange={(value) =>
                      onChildChange?.(
                        sensorGroup.parentSensorGroupId!,
                        value === 'none' ? null : Number(value),
                      )}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None (Remove Child)</SelectItem>
                      {childOptions.map((sg) => (
                        <SelectItem key={sg.sensorGroupId} value={sg.sensorGroupId.toString()}>
                          {renderSensorGroupOption(sg)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
                : hasChild
                ? (
                  // When this is a master in a relationship, show editable master selector
                  <Select
                    value={sensorGroup.sensorGroupId.toString()}
                    onValueChange={(value) =>
                      onStandaloneChange?.(
                        sensorGroup.sensorGroupId,
                        value === 'none' ? null : Number(value),
                      )}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None (Remove Master)</SelectItem>
                      {masterOptions.map((sg) => (
                        <SelectItem key={sg.sensorGroupId} value={sg.sensorGroupId.toString()}>
                          {renderSensorGroupOption(sg)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
                : (
                  // When this is standalone, show editable standalone selector
                  <Select
                    value={sensorGroup.sensorGroupId.toString()}
                    onValueChange={(value) =>
                      onStandaloneChange?.(
                        sensorGroup.sensorGroupId,
                        value === 'none' ? null : Number(value),
                      )}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None (Remove Assignment)</SelectItem>
                      {standaloneOptions.map((sg) => (
                        <SelectItem key={sg.sensorGroupId} value={sg.sensorGroupId.toString()}>
                          {renderSensorGroupOption(sg)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
            </div>
            {hasParent
              ? (
                // For DMA relationships, don't show "Remove Child" button here
                // The "Remove Association" button on the master row already handles this
                hasDMAInRelationship ? <div className='w-48 shrink-0' /> : (
                  <Button
                    variant='outline'
                    size='sm'
                    className='border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground w-48 shrink-0 justify-start'
                    onClick={() => onChildChange?.(sensorGroup.parentSensorGroupId!, null)}
                    title='Remove child (break relationship)'
                  >
                    <Trash2 className='h-4 w-4 mr-1' />
                    Remove Child
                  </Button>
                )
              )
              : !hasChild
              ? (
                <Button
                  variant='outline'
                  size='sm'
                  className='border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground w-48 shrink-0 justify-start'
                  onClick={() => onStandaloneChange?.(sensorGroup.sensorGroupId, null)}
                  title='Remove Assignment from property'
                >
                  <Trash2 className='h-4 w-4 mr-1' />
                  Remove Assignment
                </Button>
              )
              : <div className='w-48 shrink-0' />}
          </div>

          {/* Child Sensor Group Row */}
          {hasChild
            ? (
              <div className='flex items-center gap-2'>
                <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wide w-24 shrink-0 text-right'>
                  Child:
                </span>
                <div className='flex-1'>
                  <Select
                    value={sensorGroup.childSensorGroupId?.toString() || 'none'}
                    onValueChange={(value) =>
                      onChildChange?.(
                        sensorGroup.sensorGroupId,
                        value === 'none' ? null : Number(value),
                      )}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None (Remove Child)</SelectItem>
                      {childOptions.map((sg) => (
                        <SelectItem key={sg.sensorGroupId} value={sg.sensorGroupId.toString()}>
                          {renderSensorGroupOption(sg)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* For DMA relationships, hide "Remove Child" button */}
                {hasDMAInRelationship ? <div className='w-48 shrink-0' /> : (
                  <Button
                    variant='outline'
                    size='sm'
                    className='border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground w-48 shrink-0 justify-start'
                    onClick={() => onChildChange?.(sensorGroup.sensorGroupId, null)}
                    title='Remove child (break relationship)'
                  >
                    <Trash2 className='h-4 w-4 mr-1' />
                    Remove Child
                  </Button>
                )}
              </div>
            )
            : !hasParent
            ? (
              <div className='flex items-center gap-2'>
                <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wide w-24 shrink-0 text-right'>
                  Child:
                </span>
                <div className='flex-1'>
                  <Select
                    value='none'
                    onValueChange={(value) =>
                      onChildChange?.(
                        sensorGroup.sensorGroupId,
                        value === 'none' ? null : Number(value),
                      )}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select child group...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None</SelectItem>
                      {childOptions.map((sg) => (
                        <SelectItem key={sg.sensorGroupId} value={sg.sensorGroupId.toString()}>
                          {renderSensorGroupOption(sg)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='w-48 shrink-0' />
              </div>
            )
            : null}
        </div>
      </div>
    </div>
  );
}
